/**
 * The tax details an affiliate gives before it is paid past 2 400 € in a
 * calendar year (`affiliate-tax.ts` on the Worker): legal name, address,
 * country, and a SIRET in France or a registration or VAT number elsewhere.
 * Shown when the threshold is in sight or the details were given; a payout
 * past it waits for them.
 */
import { useState } from 'react';
import type React from 'react';
import { color } from '../theme/tokens';
import { Callout, Field, Input, PrimaryButton, StatusCell } from '../components/primitives';
import { SectionHead } from '../components/Section';
import { notify } from '../components/Toast';
import { api, ApiError } from '../lib/api';
import { money } from '../lib/format';

export interface TaxDetails {
  legalName: string;
  address: string;
  postalCode: string;
  city: string;
  country: string;
  siret: string | null;
  registration: string | null;
  givenAt: string;
}

export interface TaxState {
  thresholdEur: number;
  thresholdCents: number;
  paidThisYearCents: number;
  required: boolean;
  details: TaxDetails | null;
}

export function TaxDetailsSection({
  tax,
  countries,
  onSaved
}: {
  tax: TaxState;
  countries: string[];
  onSaved: (details: TaxDetails) => void;
}): React.JSX.Element | null {
  const given = tax.details;
  const [form, setForm] = useState({
    legalName: given?.legalName ?? '',
    address: given?.address ?? '',
    postalCode: given?.postalCode ?? '',
    city: given?.city ?? '',
    country: given?.country ?? 'FR',
    siret: given?.siret ?? '',
    registration: given?.registration ?? ''
  });
  const [busy, setBusy] = useState(false);
  if (!tax.required && !given) return null;

  const set = (key: keyof typeof form) => (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm({ ...form, [key]: event.target.value });
  const save = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setBusy(true);
    try {
      const answer = await api.post<{ taxDetails: TaxDetails }>('/affiliate/tax-details', form);
      onSaved(answer.taxDetails);
      notify('Tax details saved.');
    } catch (error) {
      notify(error instanceof ApiError ? error.message : 'They could not be saved.');
    } finally {
      setBusy(false);
    }
  };
  const france = form.country === 'FR';

  return (
    <>
      <SectionHead
        title="Tax details"
        description={`Past ${tax.thresholdEur.toLocaleString('en-GB')} € paid to you in a calendar year, Aircodr must report you to the French tax administration. Payouts beyond it wait until these are given. Paid to you this year: ${money(tax.paidThisYearCents, 'usd')}.`}
      />
      {given ? (
        <StatusCell tone="ok" word="Given" detail={`${given.legalName} · ${given.siret ? `SIRET ${given.siret}` : given.registration}`} />
      ) : (
        <Callout tone="amber" title="Your next payout needs these">
          You are about to pass {tax.thresholdEur.toLocaleString('en-GB')} € this year. Give your business details below and
          the payout goes out on the 1st as usual.
        </Callout>
      )}
      <form onSubmit={(event) => void save(event)} style={{ display: 'grid', gap: 14, maxWidth: 560, marginTop: 16 }}>
        <Field label="Legal name (your company, or your own name as a sole trader)" htmlFor="tax-name">
          <Input id="tax-name" value={form.legalName} onChange={set('legalName')} required maxLength={160} />
        </Field>
        <Field label="Address" htmlFor="tax-address">
          <Input id="tax-address" value={form.address} onChange={set('address')} required maxLength={200} />
        </Field>
        <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: 12 }}>
          <Field label="Postal code" htmlFor="tax-postal">
            <Input id="tax-postal" value={form.postalCode} onChange={set('postalCode')} required maxLength={16} />
          </Field>
          <Field label="City" htmlFor="tax-city">
            <Input id="tax-city" value={form.city} onChange={set('city')} required maxLength={80} />
          </Field>
        </div>
        <Field label="Country of tax residence" htmlFor="tax-country">
          <select
            id="tax-country"
            value={form.country}
            onChange={set('country')}
            style={{ height: 42, padding: '0 11px', border: `1px solid ${color.lineInput}`, borderRadius: 10, background: color.page, fontFamily: 'inherit', fontSize: 14, color: color.ink }}
          >
            {countries.map((code) => (
              <option key={code} value={code}>
                {code}
              </option>
            ))}
          </select>
        </Field>
        {france ? (
          <Field label="SIRET (14 digits)" htmlFor="tax-siret">
            <Input id="tax-siret" inputMode="numeric" value={form.siret} onChange={set('siret')} required maxLength={20} />
          </Field>
        ) : (
          <Field label="Business registration or VAT number" htmlFor="tax-registration">
            <Input id="tax-registration" value={form.registration} onChange={set('registration')} required maxLength={40} />
          </Field>
        )}
        <div>
          <PrimaryButton type="submit" size="sm" busy={busy}>
            Save tax details
          </PrimaryButton>
        </div>
      </form>
    </>
  );
}
